import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    clientType: '',
    installationType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mapAddress = `${contactInfo.address}, ${contactInfo.city}`;
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(mapAddress)}&output=embed`;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, formData);

      if (response.data.success) {
        toast.success('¡Solicitud enviada!', {
          description: 'Nos pondremos en contacto contigo pronto.',
        });

        setFormData({
          name: '',
          phone: '',
          email: '',
          clientType: '',
          installationType: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Error al enviar la solicitud', {
        description: 'Por favor, intenta de nuevo o contáctanos por teléfono.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-[#F2F2F2]">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A192F] mb-4">
            Solicita tu{' '}
            <span className="text-[#FFD700]">presupuesto sin compromiso</span>
          </h2>
          <p className="text-lg text-[#0A192F]/80 max-w-3xl mx-auto">
            Nuestro equipo técnico te asesorará para encontrar la solución ideal. Fabricamos, instalamos y reparamos en toda España.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-[#0A192F]">Nombre y apellidos *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-2"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="text-[#0A192F]">Teléfono *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#0A192F]">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="clientType" className="text-[#0A192F]">Tipo de cliente *</Label>
                  <Select
                    value={formData.clientType}
                    onValueChange={(value) => handleSelectChange('clientType', value)}
                    required
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Selecciona..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="particular">Particular</SelectItem>
                      <SelectItem value="empresa">Empresa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="installationType" className="text-[#0A192F]">Tipo de instalación</Label>
                  <Select
                    value={formData.installationType}
                    onValueChange={(value) => handleSelectChange('installationType', value)}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Selecciona..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="terrazas">Toldos para terrazas</SelectItem>
                      <SelectItem value="ventanas">Toldos para ventanas</SelectItem>
                      <SelectItem value="comercios">Toldos para comercios</SelectItem>
                      <SelectItem value="domotica">Motores y domótica</SelectItem>
                      <SelectItem value="industrial">Grandes superficies</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-[#0A192F]">Mensaje</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="mt-2"
                  placeholder="Cuéntanos sobre tu proyecto..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#FFD700] text-[#0A192F] hover:bg-[#FFD700]/90 font-semibold py-6 text-lg disabled:opacity-50"
              >
                {isSubmitting ? 'Enviando...' : 'Solicitar presupuesto ahora'}
              </Button>
            </form>
          </div>

          <div className="space-y-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-[#0A192F] mb-6">Datos de contacto</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FFD700]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <p className="text-[#0A192F] font-semibold mb-1">Email</p>
                    <a href={`mailto:${contactInfo.email}`} className="text-[#0A192F]/70 hover:text-[#FFD700]">
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FFD700]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <p className="text-[#0A192F] font-semibold mb-1">Teléfono</p>
                    <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-[#0A192F]/70 hover:text-[#FFD700] block">
                      {contactInfo.phone}
                    </a>
                    <a href={`tel:${contactInfo.mobile.replace(/\s/g, '')}`} className="text-[#0A192F]/70 hover:text-[#FFD700] block">
                      {contactInfo.mobile}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FFD700]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <p className="text-[#0A192F] font-semibold mb-1">Dirección</p>
                    <p className="text-[#0A192F]/70">
                      {contactInfo.address}<br />
                      {contactInfo.city}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FFD700]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <p className="text-[#0A192F] font-semibold mb-1">Horario</p>
                    <p className="text-[#0A192F]/70">{contactInfo.schedule}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-lg">
              <div className="w-full h-64 rounded-lg overflow-hidden">
                <iframe
                  src={mapSrc}
                  title={`Ubicación de ${mapAddress}`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapAddress)}`}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-block text-sm text-[#0A192F] hover:text-[#FFD700]"
              >
                Ver ubicación en Google Maps
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
